import axios from 'axios';

import apiClient from '@/lib/apiClient';
import { type ApiSubItem, type ProductDiscount, type SetupQuoteResult } from '@/lib/sales-metrics';
import { ApiError, ApiResponse } from '@/types';

export type DiscountMode = 'amount' | 'percent';

export interface AdditionalArnQuoteInput {
  arn: string;
  clientName: string;
  aum: number;
  /** Years this ARN is registered for; multiplies its fee. */
  termYears: number;
}

export interface SetupQuotePayload {
  // Raw product selection; the server prices every line from the catalogue
  // (bug 2), so no pre-summed flat subtotal is sent. planTier / term / discounts
  // are derived server-side from the selection.
  selectedProducts: string[];
  customPrices?: Record<string, number>;
  productTerms?: Record<string, number>;
  productDiscounts?: Record<string, ProductDiscount>;
  /** Per-line multi-year discount overrides (percent off years 2+). */
  productMultiYearDiscounts?: Record<string, number>;
  primaryArnAum?: number;
  additionalArns: AdditionalArnQuoteInput[];
  primaryArn?: string;
  /**
   * The order being quoted. Sent so the server can recognise an UPGRADE and
   * prorate it; the proration itself is resolved server-side from that order,
   * so this names which order to price, never what it should cost.
   */
  orderId?: number;
}

export interface ArnCrmValidationResult {
  exists: boolean;
  clientName: string | null;
}

export interface PricingConfig {
  multiYearRenewalDiscountPercent: number;
  /** Admin-configured sub-item catalogue per API bundle product. */
  apiSubItems: Record<string, ApiSubItem[]>;
  /** Admin-configured price options per website tier (CUSTOM = empty = free-text). */
  websitePriceSlabs: Record<string, number[]>;
}

export interface MfboxPricingSlabRow {
  id: number;
  rateCard: string;
  band: string;
  /**
   * The band's numeric bounds in crores, kept alongside the printed `band`.
   *
   * `band` is a label and cannot be compared - anything that has to decide WHICH band
   * an AUM figure falls into (the MIS-MFD revenue estimate prices a whole slab
   * distribution, not one client) would otherwise have to parse the string back into
   * numbers, which is exactly how a rate card starts being read two different ways.
   */
  aumLowerCrores: number;
  aumUpperCrores: number;
  setupPrice: number;
  renewalPrice: number;
}

interface PricingSlabApiRow {
  id: number;
  rateCard: string;
  aumLowerCrores: number | string;
  aumUpperCrores: number | string;
  setupPrice: number | string;
  renewalPrice: number | string;
}

const mapMfboxSlabRow = (slab: PricingSlabApiRow): MfboxPricingSlabRow => ({
  id: slab.id,
  rateCard: slab.rateCard,
  band: `${Number(slab.aumLowerCrores)} – ${Number(slab.aumUpperCrores)} Cr`,
  aumLowerCrores: Number(slab.aumLowerCrores),
  aumUpperCrores: Number(slab.aumUpperCrores),
  setupPrice: Math.round(Number(slab.setupPrice)),
  renewalPrice: Math.round(Number(slab.renewalPrice)),
});

export interface NewSlabPayload {
  rateCard: string;
  aumLowerCrores: number;
  aumUpperCrores: number;
  setupPrice: number;
  renewalPrice: number;
  effectiveFrom: string;
}

export const pricingService = {
  getConfig: async (): Promise<PricingConfig> => {
    const { data } = await apiClient.get<ApiResponse<PricingConfig>>('/pricing/config');
    if (!data.data) throw new Error('Pricing config returned no data');
    return data.data;
  },

  getSlabs: async (): Promise<MfboxPricingSlabRow[]> => {
    const { data } = await apiClient.get<ApiResponse<PricingSlabApiRow[]>>('/pricing/slabs');
    if (!data.data) throw new Error('Pricing slabs returned no data');
    return data.data.map(mapMfboxSlabRow);
  },

  calculateSetup: async (payload: SetupQuotePayload): Promise<SetupQuoteResult> => {
    try {
      const { data } = await apiClient.post<ApiResponse<SetupQuoteResult>>(
        '/pricing/calculate-setup',
        payload,
      );
      if (!data.data) throw new Error('Pricing calculation returned no data');
      return data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const apiError = error.response?.data as ApiError | undefined;
        throw new Error(apiError?.message ?? 'Pricing calculation failed');
      }
      throw error;
    }
  },

  validateArnInCrm: async (arn: string): Promise<ArnCrmValidationResult> => {
    const { data } = await apiClient.get<ApiResponse<ArnCrmValidationResult>>(
      `/pricing/validate-arn/${encodeURIComponent(arn)}`,
    );
    if (!data.data) throw new Error('ARN validation returned no data');
    return data.data;
  },

  createSlabs: async (slabs: NewSlabPayload[]): Promise<void> => {
    await apiClient.post('/admin/pricing-slabs', { slabs });
  },

  deleteSlabById: async (id: number): Promise<void> => {
    await apiClient.delete(`/admin/pricing-slabs/${id}`);
  },
};
